// ─────────────────────────────────────────────────────────────
// Empty result constants — use as loading/fallback states in UI
// ─────────────────────────────────────────────────────────────

export const EMPTY_PROGRESS_NARRATIVE = Object.freeze({
  narrative: '',
  basedOn: [],
});

export const EMPTY_NEXT_STEP = Object.freeze({
  explanation: '',
  confidence: 'low',
  basedOn: [],
});

export const EMPTY_FOLLOW_UP_DRAFT = Object.freeze({
  draft: '',
  editableFields: { clientName: '', serviceName: '', timing: '' },
});

export const EMPTY_LESSON_PLAN = Object.freeze({
  stages: [],
  focus: '',
  drillSuggestions: [],
  notes: '',
});

export const EMPTY_PACKAGE_EXPLANATION = Object.freeze({
  explanation: '',
  value: '',
});

export const EMPTY_RETENTION_NARRATIVE = Object.freeze({
  narrative: '',
  suggestedAction: '',
});

export const EMPTY_REVIEW_SUMMARY = Object.freeze({
  summary: '',
  studentNote: '',
  nextStepRecommendation: '',
});

// ─────────────────────────────────────────────────────────────
// Internal: HTTP helpers (same pattern as aiReviewApi.js)
// ─────────────────────────────────────────────────────────────

function getApiBaseUrl() {
  const url = process.env.REACT_APP_API_BASE_URL;
  if (!url) throw new Error('REACT_APP_API_BASE_URL is not configured.');
  return url;
}

async function getApiError(response, fallbackMessage) {
  try {
    const payload = await response.json();
    if (payload?.detail) return payload.detail;
  } catch (_) {
    // Ignore parse errors — fall through to fallback.
  }
  return fallbackMessage;
}

async function callAiEndpoint(endpoint, body) {
  const API_BASE_URL = getApiBaseUrl();

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const message = await getApiError(
      response,
      `AI request to ${endpoint} failed with status ${response.status}`
    );
    throw new Error(message);
  }

  return response.json();
}

// ─────────────────────────────────────────────────────────────
// Internal: Signal extraction — builds a bounded, safe subset
// of the full signals object to send to the backend.
// Sensitive fields (email, raw note text) are never included.
// Arrays are capped to prevent unbounded payloads.
// ─────────────────────────────────────────────────────────────

function buildSignalPayload(signals) {
  const s = signals || {};
  return {
    sport: s.sport || '',
    skillLevel: s.skillLevel || '',
    dataCompleteness: s.dataCompleteness || 'minimal',
    progressionSummary: {
      currentLevel: s.progressionSummary?.currentLevel || '',
      currentSkill: s.progressionSummary?.currentSkill?.name || null,
      nextSkill: s.progressionSummary?.nextSkill?.name || null,
      activeTrack: s.progressionSummary?.activeTrack?.label || null,
      topFocusAreas: (s.progressionSummary?.topFocusAreas || [])
        .slice(0, 4)
        .map((f) => f.label || f.key || ''),
      recommendationReason: s.progressionSummary?.recommendationReason || '',
      completedLessonCount: s.progressionSummary?.completedLessonCount || 0,
    },
    recurringIssues: (s.recurringIssues || []).slice(0, 5),
    recurringStrengths: (s.recurringStrengths || []).slice(0, 5),
    recentDrills: (s.recentDrills || [])
      .slice(0, 4)
      .map((d) => d.topic || d.drillId || ''),
    reviewedCount: s.reviewedCount || 0,
    hasUpcomingBooking: s.hasUpcomingBooking || false,
    daysSinceLastActivity: s.daysSinceLastActivity || 0,
    averageCadenceDays: s.averageCadenceDays || null,
    remainingSessions: s.remainingSessions || 0,
    remainingCredits: s.remainingCredits || 0,
    packageExpiryRisk: s.packageExpiryRisk || false,
    packageNames: (s.packageNames || []).slice(0, 3),
    retentionRisk: {
      level: s.retentionRisk?.level || 'low',
      reasons: (s.retentionRisk?.reasons || []).slice(0, 3),
    },
    goals: s.goals || '',
    tags: (s.tags || []).slice(0, 5),
    heuristicRecommendations: {
      coachingFocus: s.heuristicRecommendations?.coachingFocus || '',
      rebookTiming: s.heuristicRecommendations?.rebookTiming?.message || '',
      nextServiceName: s.heuristicRecommendations?.nextService?.name || '',
    },
    internalNotesCount: s.internalNotesCount || 0,
  };
}

// ─────────────────────────────────────────────────────────────
// Internal: Hallucination guard — injected into every request.
// The system instruction adjusts its own caution level based
// on dataCompleteness so the AI tightens its output when
// there is little history to ground it.
// ─────────────────────────────────────────────────────────────

function buildSystemInstruction(signals) {
  const completeness = (signals || {}).dataCompleteness || 'minimal';
  const cautionNote =
    completeness === 'minimal'
      ? ' Data for this client is limited — keep all recommendations cautious and general. Do not infer patterns from a single data point.'
      : completeness === 'partial'
        ? ' Some coaching history exists but is incomplete — qualify recommendations where the data is thin.'
        : '';

  return (
    'You are assisting a qualified sports instructor on a coaching platform. ' +
    'Your role is to help the instructor communicate clearly with their client. ' +
    'Only use the structured signals provided in this request. ' +
    'Do not invent technical advice, movement cues, or coaching recommendations ' +
    'not supported by the input signals. ' +
    'Do not fabricate client history, session details, prices, or package names. ' +
    'Do not present guesses as facts.' +
    cautionNote
  );
}

// ─────────────────────────────────────────────────────────────
// Internal: Deterministic derived fields — never from AI output.
// These are the explainability anchors the UI exposes to instructors.
// ─────────────────────────────────────────────────────────────

function deriveBasedOn(signals) {
  const s = signals || {};
  const sources = [];
  if ((s.reviewedCount || 0) > 0) sources.push('reviews');
  if ((s.progressionSummary?.completedLessonCount || 0) > 0) sources.push('lessons');
  if (s.hasUpcomingBooking || (s.daysSinceLastActivity || 999) < 90) sources.push('bookings');
  if ((s.recurringIssues || []).length > 0 || (s.recurringStrengths || []).length > 0) {
    sources.push('analysis');
  }
  return sources.length > 0 ? sources : ['profile'];
}

function deriveConfidence(signals) {
  const completeness = (signals || {}).dataCompleteness || 'minimal';
  if (completeness === 'full') return 'high';
  if (completeness === 'partial') return 'medium';
  return 'low';
}

function deriveFollowUpEditableFields(signals) {
  const s = signals || {};
  return {
    clientName: s.displayName || '',
    serviceName: s.heuristicRecommendations?.nextService?.name || '',
    timing: s.heuristicRecommendations?.rebookTiming?.message || '',
  };
}

// ─────────────────────────────────────────────────────────────
// Internal: Instructor context — appended to taskInstruction when present.
// The clause is validated/sanitised by buildInstructorContextClause in
// instructorCopilot.js before reaching here. We only do a final length cap.
// ─────────────────────────────────────────────────────────────

function buildTaskInstructionWithContext(baseInstruction, instructorContext, instructorProfileClause) {
  let instruction = baseInstruction;

  const clause = typeof instructorContext === 'string' ? instructorContext.trim() : '';
  if (clause) {
    instruction =
      `${instruction}\n\n` +
      `Additional context provided by the coach (use to personalise the response, ` +
      `but do not treat as authoritative history — the signals are the ground truth):\n` +
      clause.slice(0, 600);
  }

  const profileClause = typeof instructorProfileClause === 'string' ? instructorProfileClause.trim() : '';
  if (profileClause) {
    instruction = `${instruction}\n\n${profileClause}`;
  }

  return instruction;
}

// ─────────────────────────────────────────────────────────────
// Internal: Per-function payload builders.
// Each one assembles the full request body: system instruction,
// signal payload, task instruction, and options.
// The backend constructs the final LLM prompt from these fields.
// ─────────────────────────────────────────────────────────────

function buildProgressNarrativePayload(signals, { instructorContext = '', instructorProfileClause = '' } = {}) {
  return {
    systemInstruction: buildSystemInstruction(signals),
    signalPayload: buildSignalPayload(signals),
    taskInstruction: buildTaskInstructionWithContext(
      'Generate a brief, encouraging progress summary for the student. ' +
      'Highlight recurring strengths first, then acknowledge recurring issues as areas ' +
      'to continue working on. Use the progression summary to frame current level and ' +
      'direction. Keep it 2–3 sentences. Write in second person directed at the student.',
      instructorContext,
      instructorProfileClause
    ),
    options: {},
  };
}

function buildNextStepPayload(signals, { instructorContext = '', instructorProfileClause = '' } = {}) {
  return {
    systemInstruction: buildSystemInstruction(signals),
    signalPayload: buildSignalPayload(signals),
    taskInstruction: buildTaskInstructionWithContext(
      'Explain in 1–2 sentences why the recommended next step makes sense for this client ' +
      'at this stage. Reference the current skill, progression direction, and any recurring ' +
      'issues. Keep it instructor-readable, not student-facing.',
      instructorContext,
      instructorProfileClause
    ),
    options: {},
  };
}

function buildFollowUpDraftPayload(signals, { tone = 'friendly', channel = 'message', instructorContext = '', instructorProfileClause = '' } = {}) {
  return {
    systemInstruction: buildSystemInstruction(signals),
    signalPayload: buildSignalPayload(signals),
    taskInstruction: buildTaskInstructionWithContext(
      `Draft a follow-up ${channel} from the instructor to the client. ` +
      `Tone: ${tone}. ` +
      'Reference the client\'s recent activity and suggested next step. ' +
      'Keep it concise and personal. ' +
      'Do not invent session details not present in the signals. ' +
      'Do not make up prices or package names not in the signals.',
      instructorContext,
      instructorProfileClause
    ),
    options: { tone, channel },
  };
}

function buildLessonPlanPayload(signals, { targetSkill = '', duration = 60, instructorContext = '', instructorProfileClause = '' } = {}) {
  return {
    systemInstruction: buildSystemInstruction(signals),
    signalPayload: buildSignalPayload(signals),
    taskInstruction: buildTaskInstructionWithContext(
      `Suggest a structured lesson plan for the target skill: ${targetSkill || 'the recommended next skill'}. ` +
      `Duration: ${duration} minutes. ` +
      'Use the client\'s current progression level, recurring issues, and recent drills to frame the stages. ' +
      'Do not invent drills or techniques not already signalled in the input. ' +
      'Suggest a focus theme and brief coaching notes.',
      instructorContext,
      instructorProfileClause
    ),
    options: { targetSkill, duration },
  };
}

function buildPackageExplanationPayload(signals, { packageId = '', instructorContext = '' } = {}) {
  return {
    systemInstruction: buildSystemInstruction(signals),
    signalPayload: buildSignalPayload(signals),
    taskInstruction: buildTaskInstructionWithContext(
      `Explain in 1–2 sentences why the package (id: ${packageId}) is a good fit for this client. ` +
      'Reference remaining balance, expiry risk if relevant, and coaching frequency. ' +
      'Keep it persuasive but factual. ' +
      'Only reference package names present in the signals.',
      instructorContext
    ),
    options: { packageId },
  };
}

function buildRetentionNarrativePayload(signals, { instructorContext = '', instructorProfileClause = '' } = {}) {
  return {
    systemInstruction: buildSystemInstruction(signals),
    signalPayload: buildSignalPayload(signals),
    taskInstruction: buildTaskInstructionWithContext(
      'Write a brief internal note (1–2 sentences) for the instructor explaining why this ' +
      'client is flagged for retention attention. Reference the retention risk reasons and ' +
      'inactivity signals. Write in third person. Suggest one practical action.',
      instructorContext,
      instructorProfileClause
    ),
    options: {},
  };
}

function buildReviewSummaryPayload(signals, instructorContext, instructorProfileClause = '') {
  return {
    systemInstruction: buildSystemInstruction(signals),
    signalPayload: buildSignalPayload(signals),
    taskInstruction: buildTaskInstructionWithContext(
      'Based on the coach notes and client signals, produce three outputs: ' +
      '(1) "summary": a 2–3 sentence internal review summary for the instructor, written in third person. ' +
      '(2) "studentNote": a 1–2 sentence encouraging progress note to share with the student, written in second person. ' +
      '(3) "nextStepRecommendation": a single sentence recommending the most logical next coaching step. ' +
      'Do not fabricate session details not present in the signals or coach notes.',
      instructorContext,
      instructorProfileClause
    ),
    options: {},
  };
}

// ─────────────────────────────────────────────────────────────
// Internal: Guard helpers
// ─────────────────────────────────────────────────────────────

function assertClientUserId(signals, fnName) {
  if (!signals?.clientUserId) {
    throw new Error(`${fnName}: signals.clientUserId is required to construct the endpoint path.`);
  }
}

// Called before the try/catch in each exported function so a missing
// API URL is a hard fail (throws to caller) not a soft fail (empty result).
function assertHardPrerequisites(signals, fnName) {
  assertClientUserId(signals, fnName);
  getApiBaseUrl(); // throws if REACT_APP_API_BASE_URL is not set
}

function shouldSkipMinimal(signals, skipMinimal, instructorContext) {
  // If the instructor has provided context, always attempt the API call —
  // their notes compensate for limited signal data.
  if (typeof instructorContext === 'string' && instructorContext.trim()) return false;
  return skipMinimal !== false && (signals?.dataCompleteness || 'minimal') === 'minimal';
}

// ─────────────────────────────────────────────────────────────
// Exported: AI synthesis functions
// Each is independently callable. None read Firestore.
// None write to Firestore. None mutate the signals object.
// All errors are caught and return the empty result shape.
// ─────────────────────────────────────────────────────────────

/**
 * Generates a 2–3 sentence progress narrative for the student.
 * Grounded in review signals, progression summary, and lesson history.
 *
 * @param {object} signals - Output of buildClientSignals or buildClientSignalsFromWorkspace
 * @param {object} [options]
 * @param {boolean} [options.skipMinimal=true] - Skip the API call when dataCompleteness is 'minimal'
 * @returns {Promise<{ narrative: string, basedOn: string[] }>}
 */
export async function generateProgressNarrative(signals, { skipMinimal = true, instructorContext = '', instructorProfileClause = '' } = {}) {
  assertHardPrerequisites(signals, 'generateProgressNarrative');

  const basedOn = deriveBasedOn(signals);

  if (shouldSkipMinimal(signals, skipMinimal, instructorContext)) {
    return { ...EMPTY_PROGRESS_NARRATIVE, basedOn };
  }

  try {
    const result = await callAiEndpoint(
      `/ai/client/${signals.clientUserId}/progress-narrative`,
      buildProgressNarrativePayload(signals, { instructorContext, instructorProfileClause })
    );
    return {
      narrative: result.narrative || '',
      basedOn,
    };
  } catch (error) {
    console.warn('generateProgressNarrative failed:', error.message);
    return { ...EMPTY_PROGRESS_NARRATIVE, basedOn };
  }
}

/**
 * Generates a 1–2 sentence explanation of the recommended next step.
 * Confidence is derived deterministically from dataCompleteness — never from the AI.
 *
 * @param {object} signals
 * @param {object} [options]
 * @param {boolean} [options.skipMinimal=true]
 * @returns {Promise<{ explanation: string, confidence: 'high'|'medium'|'low', basedOn: string[] }>}
 */
export async function generateNextStepExplanation(signals, { skipMinimal = true, instructorContext = '', instructorProfileClause = '' } = {}) {
  assertHardPrerequisites(signals, 'generateNextStepExplanation');

  const confidence = deriveConfidence(signals);
  const basedOn = deriveBasedOn(signals);

  if (shouldSkipMinimal(signals, skipMinimal, instructorContext)) {
    return { ...EMPTY_NEXT_STEP, confidence, basedOn };
  }

  try {
    const result = await callAiEndpoint(
      `/ai/client/${signals.clientUserId}/next-step`,
      buildNextStepPayload(signals, { instructorContext, instructorProfileClause })
    );
    return {
      explanation: result.explanation || '',
      confidence,
      basedOn,
    };
  } catch (error) {
    console.warn('generateNextStepExplanation failed:', error.message);
    return { ...EMPTY_NEXT_STEP, confidence, basedOn };
  }
}

/**
 * Generates a follow-up message draft from the instructor to the client.
 * editableFields are derived from signals — not from the AI.
 * The instructor must review and edit the draft before sending.
 *
 * @param {object} signals
 * @param {object} [options]
 * @param {'friendly'|'formal'} [options.tone='friendly']
 * @param {'message'|'email'} [options.channel='message']
 * @param {boolean} [options.skipMinimal=true]
 * @returns {Promise<{ draft: string, editableFields: { clientName: string, serviceName: string, timing: string } }>}
 */
export async function generateFollowUpDraft(signals, { tone = 'friendly', channel = 'message', skipMinimal = true, instructorContext = '', instructorProfileClause = '' } = {}) {
  assertHardPrerequisites(signals, 'generateFollowUpDraft');

  const editableFields = deriveFollowUpEditableFields(signals);

  if (shouldSkipMinimal(signals, skipMinimal, instructorContext)) {
    return { ...EMPTY_FOLLOW_UP_DRAFT, editableFields };
  }

  try {
    const result = await callAiEndpoint(
      `/ai/client/${signals.clientUserId}/follow-up-draft`,
      buildFollowUpDraftPayload(signals, { tone, channel, instructorContext, instructorProfileClause })
    );
    return {
      draft: result.draft || '',
      editableFields,
    };
  } catch (error) {
    console.warn('generateFollowUpDraft failed:', error.message);
    return { ...EMPTY_FOLLOW_UP_DRAFT, editableFields };
  }
}

/**
 * Generates a suggested lesson plan for a target skill.
 * The instructor must edit stages and drills before saving to lessonInstances.
 *
 * @param {object} signals
 * @param {object} [options]
 * @param {string} [options.targetSkill=''] - Skill name or id to plan around
 * @param {number} [options.duration=60] - Session duration in minutes
 * @param {boolean} [options.skipMinimal=true]
 * @returns {Promise<{ stages: Array, focus: string, drillSuggestions: Array, notes: string }>}
 */
export async function generateLessonPlanSuggestion(signals, { targetSkill = '', duration = 60, skipMinimal = true, instructorContext = '', instructorProfileClause = '' } = {}) {
  assertHardPrerequisites(signals, 'generateLessonPlanSuggestion');

  if (shouldSkipMinimal(signals, skipMinimal, instructorContext)) {
    return { ...EMPTY_LESSON_PLAN };
  }

  try {
    const result = await callAiEndpoint(
      `/ai/client/${signals.clientUserId}/lesson-plan`,
      buildLessonPlanPayload(signals, { targetSkill, duration, instructorContext, instructorProfileClause })
    );
    return {
      stages: Array.isArray(result.stages) ? result.stages : [],
      focus: result.focus || '',
      drillSuggestions: Array.isArray(result.drillSuggestions) ? result.drillSuggestions : [],
      notes: result.notes || '',
    };
  } catch (error) {
    console.warn('generateLessonPlanSuggestion failed:', error.message);
    return { ...EMPTY_LESSON_PLAN };
  }
}

/**
 * Generates a 1–2 sentence explanation of why a specific package suits this client.
 *
 * @param {object} signals
 * @param {object} options
 * @param {string} options.packageId - Required. The package to explain.
 * @param {boolean} [options.skipMinimal=true]
 * @returns {Promise<{ explanation: string, value: string }>}
 */
export async function generatePackageExplanation(signals, { packageId = '', skipMinimal = true } = {}) {
  assertHardPrerequisites(signals, 'generatePackageExplanation');

  if (!packageId) {
    throw new Error('generatePackageExplanation: packageId is required.');
  }

  if (shouldSkipMinimal(signals, skipMinimal)) {
    return { ...EMPTY_PACKAGE_EXPLANATION };
  }

  try {
    const result = await callAiEndpoint(
      `/ai/client/${signals.clientUserId}/package-explanation`,
      buildPackageExplanationPayload(signals, { packageId })
    );
    return {
      explanation: result.explanation || '',
      value: result.value || '',
    };
  } catch (error) {
    console.warn('generatePackageExplanation failed:', error.message);
    return { ...EMPTY_PACKAGE_EXPLANATION };
  }
}

/**
 * Generates a brief internal retention note for the instructor.
 * Intended for display in the retention workspace, not shown to the client.
 *
 * @param {object} signals
 * @param {object} [options]
 * @param {boolean} [options.skipMinimal=true]
 * @returns {Promise<{ narrative: string, suggestedAction: string }>}
 */
export async function generateRetentionRiskNarrative(signals, { skipMinimal = true, instructorContext = '', instructorProfileClause = '' } = {}) {
  assertHardPrerequisites(signals, 'generateRetentionRiskNarrative');

  if (shouldSkipMinimal(signals, skipMinimal, instructorContext)) {
    return { ...EMPTY_RETENTION_NARRATIVE };
  }

  try {
    const result = await callAiEndpoint(
      `/ai/client/${signals.clientUserId}/retention-narrative`,
      buildRetentionNarrativePayload(signals, { instructorContext, instructorProfileClause })
    );
    return {
      narrative: result.narrative || '',
      suggestedAction: result.suggestedAction || '',
    };
  } catch (error) {
    console.warn('generateRetentionRiskNarrative failed:', error.message);
    return { ...EMPTY_RETENTION_NARRATIVE };
  }
}

/**
 * Generates a review summary from instructor's rough notes + client signals.
 * Used in the feedback/review workflow as an optional drafting aid.
 * Instructor context is REQUIRED here — this function is specifically for
 * the copilot drafting flow where the instructor has typed notes.
 *
 * @param {object} signals
 * @param {string} instructorContext - Built by buildInstructorContextClause() in instructorCopilot.js
 * @param {object} [options]
 * @param {boolean} [options.skipMinimal=false] - Defaults false: instructor context compensates
 * @returns {Promise<{ summary: string, studentNote: string, nextStepRecommendation: string }>}
 */
export async function generateReviewSummaryFromNotes(signals, instructorContext, { skipMinimal = false, instructorProfileClause = '' } = {}) {
  assertHardPrerequisites(signals, 'generateReviewSummaryFromNotes');

  if (shouldSkipMinimal(signals, skipMinimal, instructorContext)) {
    return { ...EMPTY_REVIEW_SUMMARY };
  }

  try {
    const result = await callAiEndpoint(
      `/ai/client/${signals.clientUserId}/review-summary`,
      buildReviewSummaryPayload(signals, instructorContext, instructorProfileClause)
    );
    return {
      summary: result.summary || '',
      studentNote: result.studentNote || '',
      nextStepRecommendation: result.nextStepRecommendation || '',
    };
  } catch (error) {
    console.warn('generateReviewSummaryFromNotes failed:', error.message);
    return { ...EMPTY_REVIEW_SUMMARY };
  }
}

// ─────────────────────────────────────────────────────────────
// Exported: Safe normalizers for each result type.
// Use when consuming results from an external source (e.g. cache)
// where field presence cannot be guaranteed.
// ─────────────────────────────────────────────────────────────

export function normalizeAiInsightResult(type, result) {
  const r = result || {};

  switch (type) {
    case 'progressNarrative':
      return {
        narrative: r.narrative || '',
        basedOn: Array.isArray(r.basedOn) ? r.basedOn : [],
      };

    case 'nextStep':
      return {
        explanation: r.explanation || '',
        confidence: ['high', 'medium', 'low'].includes(r.confidence) ? r.confidence : 'low',
        basedOn: Array.isArray(r.basedOn) ? r.basedOn : [],
      };

    case 'followUpDraft':
      return {
        draft: r.draft || '',
        editableFields: {
          clientName: r.editableFields?.clientName || '',
          serviceName: r.editableFields?.serviceName || '',
          timing: r.editableFields?.timing || '',
        },
      };

    case 'lessonPlan':
      return {
        stages: Array.isArray(r.stages) ? r.stages : [],
        focus: r.focus || '',
        drillSuggestions: Array.isArray(r.drillSuggestions) ? r.drillSuggestions : [],
        notes: r.notes || '',
      };

    case 'packageExplanation':
      return {
        explanation: r.explanation || '',
        value: r.value || '',
      };

    case 'retentionNarrative':
      return {
        narrative: r.narrative || '',
        suggestedAction: r.suggestedAction || '',
      };

    case 'reviewSummary':
      return {
        summary: r.summary || '',
        studentNote: r.studentNote || '',
        nextStepRecommendation: r.nextStepRecommendation || '',
      };

    default:
      return r;
  }
}
